﻿import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, CheckCircle2, FileText, Loader2, Mail, Pencil, Phone, Plus, Users, XCircle } from 'lucide-react';
import type { CandidateStage } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { recruitmentService } from '@/services/recruitment.service';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatCard } from '@/components/ui/stat-card';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { Avatar, StageBadge } from './shared';
import { STAGE_META, formatDate, fullName } from './utils';
import { AddEditCandidateModal } from './AddEditCandidateModal';
import { CandidateDrawer } from './CandidateDrawer';

const STAGE_FILTERS: CandidateStage[] = [
  'new', 'screening', 'interview', 'selected', 'offer_released',
  'offer_accepted', 'offer_rejected', 'rejected', 'converted', 'on_hold',
];

export function PreJoiningTab() {
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<CandidateStage | ''>('');
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [drawerId, setDrawerId] = useState<string | null>(null);

  const statsQuery = useQuery({ queryKey: ['recruitment', 'stats'], queryFn: () => recruitmentService.stats() });
  const listQuery = useQuery({
    queryKey: ['recruitment', 'candidates', { search, stage, page }],
    queryFn: () => recruitmentService.listCandidates({ q: search || undefined, stage: stage || undefined, page, limit: 20 }),
  });

  const s = statsQuery.data?.pipeline;
  const editCandidateQuery = useQuery({
    queryKey: ['recruitment', 'candidate', editId],
    queryFn: () => recruitmentService.getCandidate(editId!),
    enabled: !!editId,
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard label="Total" value={s?.total ?? '—'} icon={Users} tone="teal" />
        <StatCard label="In Pipeline" value={s?.in_pipeline ?? '—'} icon={Briefcase} tone="sky" />
        <StatCard label="Offers Pending" value={s?.offers_pending ?? '—'} icon={FileText} tone="amber" />
        <StatCard label="Converted" value={s?.converted ?? '—'} icon={CheckCircle2} tone="emerald" />
        <StatCard label="Rejected" value={s?.rejected ?? '—'} icon={XCircle} tone="rose" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          placeholder="Search name, email, phone, code…"
          containerClassName="min-w-[240px] flex-1"
        />
        <Select
          value={stage || 'all'}
          onValueChange={(v) => { setStage(v === 'all' ? '' : (v as CandidateStage)); setPage(1); }}
        >
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGE_FILTERS.map((st) => <SelectItem key={st} value={st}>{STAGE_META[st].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <PermissionGate permission={PERMISSIONS.RECRUITMENT_MANAGE}>
          <Button onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" /> Add Candidate</Button>
        </PermissionGate>
      </div>

      <div className="rounded-lg border border-border/60 bg-card">
        {listQuery.isLoading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>
        ) : listQuery.isError ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center text-destructive">
            <XCircle className="h-8 w-8 opacity-60" />
            <span className="text-sm font-medium">Failed to load candidates</span>
            <Button size="sm" variant="outline" onClick={() => listQuery.refetch()}>Retry</Button>
          </div>
        ) : (listQuery.data?.data.length ?? 0) === 0 ? (
          <div className="flex flex-col items-center gap-1 py-16 text-center text-muted-foreground">
            <Users className="h-10 w-10 opacity-40" />
            <p className="text-sm font-medium">No candidates found</p>
            <p className="text-xs">Click “Add Candidate” to start the pipeline</p>
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {listQuery.data!.data.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setDrawerId(c.id)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40"
              >
                <Avatar first={c.first_name} last={c.last_name} />
                <div className="min-w-0 flex-[2]">
                  <p className="truncate text-sm font-semibold text-foreground">{fullName(c.first_name, c.last_name)}</p>
                  <p className="truncate text-xs text-muted-foreground">{c.candidate_code}</p>
                </div>
                <div className="hidden min-w-0 flex-[2] md:block">
                  <p className="flex items-center gap-1 truncate text-xs text-muted-foreground"><Mail className="h-3 w-3" />{c.email}</p>
                  <p className="flex items-center gap-1 truncate text-xs text-muted-foreground"><Phone className="h-3 w-3" />{c.phone}</p>
                </div>
                <div className="hidden min-w-0 flex-[2] lg:block">
                  <p className="truncate text-xs text-foreground">{c.applied_designation_name ?? '—'}</p>
                  <p className="truncate text-xs text-muted-foreground">{c.applied_department_name ?? '—'}</p>
                </div>
                <div className="hidden flex-1 text-xs text-muted-foreground lg:block">
                  <p className="uppercase">{c.source}</p>
                  <p>{formatDate(c.created_at)}</p>
                </div>
                <StageBadge stage={c.stage} />
                <PermissionGate permission={PERMISSIONS.RECRUITMENT_MANAGE}>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setEditId(c.id); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setEditId(c.id); } }}
                    className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Pencil className="h-4 w-4" />
                  </span>
                </PermissionGate>
              </button>
            ))}
          </div>
        )}
      </div>

      {listQuery.data && listQuery.data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
          <span className="text-xs text-muted-foreground">Page {page} / {listQuery.data.pagination.totalPages}</span>
          <Button size="sm" variant="outline" disabled={page >= listQuery.data.pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      {addOpen && <AddEditCandidateModal onClose={() => setAddOpen(false)} onSaved={() => setAddOpen(false)} />}
      {editId && editCandidateQuery.data && (
        <AddEditCandidateModal candidate={editCandidateQuery.data} onClose={() => setEditId(null)} onSaved={() => setEditId(null)} />
      )}
      {drawerId && (
        <CandidateDrawer
          candidateId={drawerId}
          onClose={() => setDrawerId(null)}
          onEdit={() => { setEditId(drawerId); setDrawerId(null); }}
        />
      )}
    </div>
  );
}
